import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@athena/ui";
import type { ContactChannel } from "../helpers/client";
import type { ClientOption } from "./ClientSearchField";
import { LogContactForm, type LoggedContact } from "./LogContactForm";

export type { LoggedContact } from "./LogContactForm";

interface LogContactDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Who the contact is with. Leave unset and pass `clients` to have the dialog ask instead. */
    client?: { id: string; name: string };
    /** The people to choose from when the dialog opens without one — "Log a contact" in a list. */
    clients?: ClientOption[];
    today: Date;
    /** Saves the contact. Rejects when it could not be, and the dialog stays open with what was entered. */
    onLog: (contact: LoggedContact) => Promise<void>;
    /** Starts with this channel picked — the one a queued follow-up planned. */
    defaultChannel?: ContactChannel;
}

export function LogContactDialog({
    open,
    onOpenChange,
    client,
    clients,
    today,
    onLog,
    defaultChannel,
}: LogContactDialogProps) {
    const { t } = useTranslation();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t("admin.alumni.logContact.title")}</DialogTitle>
                    <DialogDescription>
                        {client
                            ? t("admin.alumni.logContact.description", { name: client.name })
                            : t("admin.alumni.logContact.descriptionAnyone")}
                    </DialogDescription>
                </DialogHeader>
                {/* Its own component so its state resets each time the dialog opens — Radix unmounts the content on close. */}
                <LogContactForm
                    layout="dialog"
                    today={today}
                    clientId={client?.id}
                    clients={clients}
                    plannedChannel={defaultChannel}
                    onCancel={() => onOpenChange(false)}
                    onSubmit={async (contact) => {
                        await onLog(contact);
                        onOpenChange(false);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}
